import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Shimmer from 'react-shimmer-effect';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../../shared/_TBComponents/Button';
import Icon from '../../../../shared/_TBComponents/Icon';
import Text from '../../../../shared/_TBComponents/Text';
import { fetchAthleteAssignments } from '../../ducks/navigatorActions';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-width: 0;
  height: 100%;
  background: #ffffff;
  border-radius: 4px;
  padding: 16px;
  gap: 8px;
  overflow: hidden;
  box-shadow: 0 16px 24px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  border: 1px solid #eee;

  @media screen and (max-width: 1200px) {
    min-height: 250px;
  }

  @media screen and (max-width: 767px) {
    overflow: visible;
    height: auto;
    min-height: 0;
    flex: none;
  }
`;

const ScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media screen and (max-width: 767px) {
    overflow-y: visible;
  }

  &::-webkit-scrollbar {
    width: 8px;
    opacity: 0;
    transition: opacity 0.3s;
  }
  &:hover::-webkit-scrollbar {
    opacity: 1;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #999;
  }

  scrollbar-width: thin;
  scrollbar-color: #ccc #f1f1f1;
`;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
  gap: 6px;
`;

const SubContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Header = styled.div`
  display: flex;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const AssignedWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const ItemWrapper = styled.div`
  margin-left: 12px;
  font-size: 16px;
  cursor: default;
  padding-left: 12px;
  text-indent: -12px;
  line-height: 1.4;
  word-break: break-word;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
  text-align: center;
  padding: 16px;
  gap: 8px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  margin: 8px 0;
`;

const ShimmerTitle = styled.div`
  width: 240px;
  min-height: 30px;
  border-radius: 24px;
`;

const ShimmerItem = styled.div`
  width: 200px;
  min-height: 20px;
  border-radius: 24px;
  margin: 6px 0;
`;

const shimmerMapper = (sectionIndex) => (
  <SectionWrapper key={sectionIndex}>
    <Shimmer>
      <ShimmerTitle />
    </Shimmer>
    {[0, 1].map((itemIndex) => (
      <Shimmer key={`${sectionIndex}-${itemIndex}`}>
        <ShimmerItem />
      </Shimmer>
    ))}
  </SectionWrapper>
);

const AthleteAssignments = ({ selectedAthlete, manageUsers, onEditAthlete }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const athleteAssignments = useSelector((state) => state.navigator.data.selectedUserAssignments);
  const athleteAssignmentsError = useSelector(
    (state) => state.navigator.data.selectedUserAssignmentsError,
  );
  const isLoadingAthleteAssignments = useSelector(
    (state) => state.navigator.ui.isLoadingSelectedUserAssignments,
  );

  useEffect(() => {
    if (currentUser?.accountCode && selectedAthlete?.id) {
      dispatch(fetchAthleteAssignments(currentUser.accountCode, selectedAthlete.id));
    }
  }, [dispatch, currentUser?.accountCode, selectedAthlete?.id]);

  let buildWorkoutLink = null;

  if (selectedAthlete?.id) {
    const today = new Date().toISOString().slice(0, 10);
    buildWorkoutLink = `/build?athleteId=${selectedAthlete.id}&date=${today}`;
  }

  return (
    <Container>
      <Header>
        <TitleContainer>
          <Text styledTextProps={{
            fontSize: '18px',
            fontWeight: 700,
            letterSpacing: '0.5px',
            color: '#1A1A1A',
          }}
          >
            Assignments
          </Text>
          <Button
            name='Build Workout'
            icon='build'
            iconColor='#fff'
            customBorderRadius='4px'
            noBorder
            bgColor='#1A1A1A'
            txtColor='#fff'
            fontSize='16px'
            href={buildWorkoutLink}
            onClick={(e) => {
              const isModified = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
              if (isModified || !buildWorkoutLink) return;
              e.preventDefault();
              history.push(buildWorkoutLink);
            }}
            styledWrapperProps={{
              boxShadow: '0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05)',
              padding: '6px 16px',
            }}
            styledTextProps={{ fontWeight: '700', letterSpacing: '0.5px' }}
          />
        </TitleContainer>
      </Header>
      {isLoadingAthleteAssignments ? (
        <LoadingContainer>
          {[0, 1].map(shimmerMapper)}
        </LoadingContainer>
      ) : null}
      {!isLoadingAthleteAssignments && athleteAssignmentsError ? (
        <ErrorState>
          <Text styledTextProps={{ fontSize: '16px', fontWeight: 700 }}>
            Error loading assignments
          </Text>
          <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
            {athleteAssignmentsError}
          </Text>
        </ErrorState>
      ) : null}
      <ScrollContainer
        style={{ display: isLoadingAthleteAssignments || athleteAssignmentsError ? 'none' : 'flex' }}
      >

        {/* Calendar Section */}
        <SectionWrapper>
          <AssignedWrapper>
            <Text
              styledTextProps={{ fontWeight: 600 }}
              styledWrapperProps={{ margin: '8px 0' }}
            >
              Calendar Assigned
            </Text>
            {manageUsers && (
            <Button
              name={athleteAssignments?.calendar ? 'Change' : 'Assign'}
              styledTextProps={{ fontWeight: 700, letterSpacing: '0.5px' }}
              fontSize='16px'
              txtColor='#77A7FF'
              bgColor='transparent'
              noBorder
              onClick={() => onEditAthlete && onEditAthlete('calendars')}
            />
            )}
          </AssignedWrapper>
          {(() => {
            if (!athleteAssignments?.calendar) {
              return (
                <Text
                  styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}
                  styledWrapperProps={{ marginLeft: '12px' }}
                >
                  No assigned calendar
                </Text>
              );
            }
            if (athleteAssignments?.calendar?.parentCalendar) {
              return (
                <>
                  <ItemWrapper>
                    <Text styledTextProps={{ fontSize: '16px' }}>
                      {athleteAssignments?.calendar?.parentCalendar.name}
                    </Text>
                  </ItemWrapper>
                  <SubContainer>
                    <Icon
                      icon='sub-arrow'
                      size={12}
                      color='#929292'
                      styledProps={{ margin: '0px 0 6px 20px' }}
                    />
                    <ItemWrapper>{athleteAssignments?.calendar?.name}</ItemWrapper>
                  </SubContainer>
                </>
              );
            }
            return (
              <ItemWrapper>{athleteAssignments?.calendar?.name}</ItemWrapper>
            );
          })()}
        </SectionWrapper>

        {/* Programs Section */}
        <SectionWrapper>
          <AssignedWrapper>
            <Text
              styledTextProps={{ fontWeight: 600 }}
              styledWrapperProps={{ margin: '8px 0' }}
            >
              Programs Assigned
            </Text>
            {manageUsers && (
            <Button
              name={athleteAssignments?.dayBasedPrograms
                      && athleteAssignments?.dayBasedPrograms.length > 0
                ? 'Change' : 'Assign'}
              styledTextProps={{ fontWeight: 700, letterSpacing: '0.5px' }}
              fontSize='16px'
              txtColor='#77A7FF'
              bgColor='transparent'
              noBorder
              onClick={() => {
                const isAssign = !(athleteAssignments?.dayBasedPrograms?.length > 0);
                onEditAthlete && onEditAthlete('programs', isAssign);
              }}
            />
            )}
          </AssignedWrapper>
          {athleteAssignments?.dayBasedPrograms
            && athleteAssignments?.dayBasedPrograms.length > 0 ? (
              athleteAssignments?.dayBasedPrograms.map((program) => (
                <ItemWrapper key={program.id}>{program.name}</ItemWrapper>
              ))
            ) : (
              <Text
                styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}
                styledWrapperProps={{ marginLeft: '12px' }}
              >
                No assigned programs
              </Text>
            )}
        </SectionWrapper>
      </ScrollContainer>
    </Container>
  );
};

AthleteAssignments.propTypes = {
  selectedAthlete: PropTypes.oneOfType([
    PropTypes.shape({ first: PropTypes.string }),
    PropTypes.arrayOf(PropTypes.shape({ first: PropTypes.string })),
  ]),
  manageUsers: PropTypes.bool,
  onEditAthlete: PropTypes.func.isRequired,
};

AthleteAssignments.defaultProps = {
  selectedAthlete: undefined,
  manageUsers: false,
};

export default AthleteAssignments;
